import { useCallback, useEffect, JSX } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useForm, SubmitHandler, useWatch } from 'react-hook-form'

import { SplitsLink as Link } from '~/components/Link'
import { TextInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { QUERY_KEYS } from '~/constants/react-query'
import { useSetActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { AuthUserOrg } from '~/types'
import { validateOrgName } from '~/utils/org'
import { sendTrackingData } from '~/utils/tracking'

type NewOrgFormValues = {
  name: string
}

const DEFAULT_NEW_ORG_FORM = {
  name: '',
}

export type CreateOrgResponse = {
  org: AuthUserOrg
}

export const NewOrgModal = ({
  isModalOpen,
  setIsModalOpen,
}: {
  isModalOpen: boolean
  setIsModalOpen: (arg0: boolean) => void
}): JSX.Element => {
  const { setActiveOrg } = useSetActiveOrg()
  const router = useRouter()

  const { serverPostRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()

  const { control, handleSubmit, setValue } = useForm<NewOrgFormValues>({
    defaultValues: DEFAULT_NEW_ORG_FORM,
  })
  const name = useWatch({ control, name: 'name' })

  useEffect(() => {
    if (!isModalOpen) setValue('name', '')
  }, [isModalOpen, setValue])

  const onSubmit: SubmitHandler<NewOrgFormValues> = useCallback(
    async ({ name }) => {
      sendTrackingData('submitNewOrgModal')

      const result = await serverPostRequest<CreateOrgResponse>({
        path: '/api/org',
        params: {
          name,
          onboardingStep: 'MEMBERS',
          appSource: 'teams',
        },
        errorMessage: 'Failed to create new workspace',
      })
      if (result) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTH_USER] })
        setActiveOrg(result.data.org)
        setIsModalOpen(false)
        router.push('/')
      }
    },
    [serverPostRequest, queryClient, setActiveOrg, setIsModalOpen, router],
  )

  return (
    <SplitsDialogContent
      id="createOrg"
      variant="cta"
      title="Create a workspace"
      isLoading={isProcessing}
      actionButton={{
        text: 'Create workspace',
        onClick: handleSubmit(onSubmit),
        isDisabled: !name,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p>
          Start working with your teammates by creating a shared workspace. To
          learn more about workspaces, visit the{' '}
          <Link
            path="https://splits.org/docs/workspaces"
            underline
            target="tab"
          >
            docs
          </Link>
          .
        </p>
        <TextInput
          control={control}
          inputName={'name'}
          placeholder={'Splits Labs'}
          validationFunc={(name) => {
            // prevent empty string or string with only whitespace
            if (name.trim().length === 0) {
              return 'Not a valid name'
            }

            return validateOrgName(name)
          }}
          autoFocus={true}
          label="Workspace name"
        />
      </form>
    </SplitsDialogContent>
  )
}
